import { useEffect, useRef, useState } from 'react';
import { Alert, Anchor, Center, Loader, Text } from '@mantine/core';
import { Link, useSearchParams } from 'react-router-dom';

import { verifyEmail } from '../lib/auth';
import { AuthCard } from './SignInPage';

/**
 * Where the link in the confirmation mail lands.
 *
 * It spends the token on arrival — the page has nothing to ask, and a
 * confirmation that waits for a button is a confirmation somebody abandons.
 * React runs an effect twice in development, so the attempt is guarded: the
 * second run would find its own token already spent and report a dead link.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [error, setError] = useState<Error | null>(null);
  const [done, setDone] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    if (!token) {
      setError(new Error('This link is incomplete.'));
      return;
    }
    verifyEmail(token)
      .then(() => setDone(true))
      .catch(setError);
  }, [token]);

  return (
    <AuthCard title="Confirm your address">
      {error ? (
        <>
          <Alert color="red" variant="light">
            {error.message}
          </Alert>
          <Text size="sm">
            Ask for a new link on the sign-in page — the button for it appears as soon as signing in
            fails for want of a confirmation.
          </Text>
          <Anchor component={Link} to="/" size="sm">
            To sign-in
          </Anchor>
        </>
      ) : done ? (
        <>
          <Text size="sm">Thank you — the address is confirmed.</Text>
          <Anchor component={Link} to="/" size="sm">
            Sign in now
          </Anchor>
        </>
      ) : (
        <Center py="md">
          <Loader />
        </Center>
      )}
    </AuthCard>
  );
}
